/**
 * Pre-recorded demo responses for instant playback.
 * Each step has a user query and a pre-built assistant response
 * with text content and visualization data.
 */

export interface DemoResponse {
  label: string;
  userQuery: string;
  assistantText: string;
  visualization?: any;
}

export const DEMO_RESPONSES: DemoResponse[] = [
  // ── Step 1: lncRNA Profile ──
  {
    label: 'lncRNA Profile',
    userQuery: 'Tell me everything about MALAT1',
    assistantText:
      'Here is the comprehensive profile for **MALAT1** (Metastasis-Associated Lung Adenocarcinoma Transcript 1), one of the most well-studied long non-coding RNAs in human biology.\n\nMALAT1 appears across **8 diseases** in the RNAverse atlas, with its strongest signal in **lung cancer** where it ranks among the top differentially expressed lncRNAs. It is consistently **upregulated** in disease contexts with a best log₂FC of **3.21**.\n\nBelow you\'ll find its cross-disease appearances, available gene regulatory networks, and relevant literature.',
    visualization: {
      type: 'lncrna_profile',
      from_precomputed: true,
      profile: {
        lncrna: 'MALAT1',
        precomputed_at: '2026-03-15T10:00:00Z',
        summary: {
          total_appearances: 24,
          diseases_present: [
            'lung cancer',
            'breast cancer',
            'hepatocellular carcinoma',
            'colorectal cancer',
            'glioblastoma',
            'pancreatic cancer',
            'ovarian cancer',
            'prostate cancer',
          ],
          top_disease: 'lung cancer',
          top_cell_type: 'epithelial cells',
          top_dataset_id: 'dataset_lc_001',
          best_score: 0.9247,
          best_rank: 1,
          best_log2fc: 3.21,
          regulation_direction: 'upregulated',
        },
        appearances: [
          {
            disease: 'lung cancer',
            dataset_id: 'dataset_lc_001',
            cell_type: 'epithelial cells',
            rank: 1,
            log2fc: 3.21,
            adj_pvalue: 2.1e-12,
            disease_mean: 12.45,
            normal_mean: 3.18,
            regulation_direction: 'upregulated',
            has_grn: true,
            grn_dataset_id: 'dataset_lc_001',
          },
          {
            disease: 'breast cancer',
            dataset_id: 'dataset_bc_001',
            cell_type: 'luminal epithelial cells',
            rank: 3,
            log2fc: 2.87,
            adj_pvalue: 8.4e-10,
            disease_mean: 10.32,
            normal_mean: 3.05,
            regulation_direction: 'upregulated',
            has_grn: true,
            grn_dataset_id: 'dataset_bc_001',
          },
          {
            disease: 'hepatocellular carcinoma',
            dataset_id: 'dataset_hcc_001',
            cell_type: 'hepatocytes',
            rank: 2,
            log2fc: 2.94,
            adj_pvalue: 1.3e-9,
            disease_mean: 11.08,
            normal_mean: 3.41,
            regulation_direction: 'upregulated',
            has_grn: true,
          },
          {
            disease: 'colorectal cancer',
            dataset_id: 'dataset_crc_001',
            cell_type: 'colonocytes',
            rank: 5,
            log2fc: 2.15,
            adj_pvalue: 4.7e-7,
            disease_mean: 8.72,
            normal_mean: 3.62,
            regulation_direction: 'upregulated',
            has_grn: true,
          },
          {
            disease: 'glioblastoma',
            dataset_id: 'dataset_gbm_001',
            cell_type: 'astrocytes',
            rank: 4,
            log2fc: 2.53,
            adj_pvalue: 6.2e-8,
            disease_mean: 9.14,
            normal_mean: 2.89,
            regulation_direction: 'upregulated',
            has_grn: false,
          },
          {
            disease: 'pancreatic cancer',
            dataset_id: 'dataset_pc_001',
            cell_type: 'ductal cells',
            rank: 8,
            log2fc: 1.76,
            adj_pvalue: 2.1e-5,
            disease_mean: 7.23,
            normal_mean: 3.51,
            regulation_direction: 'upregulated',
            has_grn: false,
          },
        ],
        knockdown_results: [],
        literature: {
          papers: [
            {
              pmid: '29217591',
              title: 'MALAT1 as a diagnostic and therapeutic target in cancer',
              year: '2018',
              authors: 'Zhang X, Hamblin MH, Yin KJ',
              journal: 'Annual Review of Pharmacology and Toxicology',
              is_retracted: false,
            },
            {
              pmid: '31263157',
              title:
                'The multifaceted roles of MALAT1 in cancer metastasis and therapy resistance',
              year: '2019',
              authors: 'Arun G, Aggarwal D, Spector DL',
              journal: 'Cancer Research',
              is_retracted: false,
            },
            {
              pmid: '33462127',
              title: 'MALAT1 promotes tumor growth and metastasis by targeting miR-206/VEGFA axis in lung cancer',
              year: '2021',
              authors: 'Li S, Mei Z, Hu HB',
              journal: 'Cell Death & Disease',
              is_retracted: false,
            },
            {
              pmid: '35614570',
              title:
                'Long non-coding RNA MALAT1 in breast cancer: mechanisms and therapeutic implications',
              year: '2022',
              authors: 'Kim J, Piao HL, Kim BJ, et al.',
              journal: 'Molecular Cancer',
              is_retracted: false,
            },
          ],
          gemini_summary:
            'MALAT1 (Metastasis-Associated Lung Adenocarcinoma Transcript 1) is one of the most extensively studied lncRNAs in oncology. Originally identified as a prognostic marker for lung cancer metastasis, it has since been implicated in multiple cancer types including breast, liver, colorectal, and brain cancers. MALAT1 functions through diverse mechanisms: it acts as a competing endogenous RNA (ceRNA) sponging tumor-suppressive miRNAs, regulates alternative splicing by interacting with SR proteins, and modulates chromatin organization in nuclear speckles. Its overexpression consistently correlates with poor prognosis, advanced tumor stage, and therapy resistance across malignancies.',
          search_terms_used: ['MALAT1 cancer', 'MALAT1 long non-coding RNA'],
        },
        other_grn_paths: [
          {
            disease: 'ovarian cancer',
            dataset_id: 'dataset_ov_001',
            cell_type: 'epithelial cells',
            rank: 6,
            enrichment_score: 0.71,
            composite_score: 0.78,
          },
        ],
        best_grn: null,
      },
    },
  },

  // ── Step 2: Target Discovery ──
  {
    label: 'Target Discovery',
    userQuery: 'Top targets in breast cancer',
    assistantText:
      'Here are the top-ranked lncRNA therapeutic targets for **breast cancer**, identified through dual-ranking analysis combining expression-based and network-based scoring.\n\nI found **47 significant targets** across consensus, expression-dominant, and network-dominant categories. The dashboard below shows the full ranked list with regulation direction, log₂FC, and priority scores.',
    visualization: {
      type: 'disease_dashboard',
      disease: 'breast cancer',
      dataset_id: 'dataset_bc_001',
      targets: [
        { symbol: 'LINC01871', expression_rank: 1, priority_rank: 1, ranking_category: 'CONSENSUS', rank_difference: 0, log2fc: 3.45, adj_pvalue: 1.2e-8, priority_score: 94.2, priority_level: 'HIGH', has_grn: true, regulation: 'upregulated', top_cell_type: 'mast cells' },
        { symbol: 'EPHA2-AS1', expression_rank: 2, priority_rank: 2, ranking_category: 'CONSENSUS', rank_difference: 0, log2fc: 2.22, adj_pvalue: 3.8e-7, priority_score: 88.6, priority_level: 'HIGH', has_grn: true, regulation: 'upregulated', top_cell_type: 'epithelial cells' },
        { symbol: 'SNHG14', expression_rank: 4, priority_rank: 3, ranking_category: 'TOP_NETWORK', rank_difference: 1, log2fc: 2.96, adj_pvalue: 5.1e-9, priority_score: 82.4, priority_level: 'HIGH', has_grn: true, regulation: 'upregulated', top_cell_type: 'luminal cells' },
        { symbol: 'MIR22HG', expression_rank: 3, priority_rank: 4, ranking_category: 'TOP_EXPRESSION', rank_difference: -1, log2fc: 2.51, adj_pvalue: 7.4e-8, priority_score: 79.1, priority_level: 'HIGH', has_grn: true, regulation: 'upregulated', top_cell_type: 'basal cells' },
        { symbol: 'OIP5-AS1', expression_rank: 6, priority_rank: 5, ranking_category: 'CONSENSUS', rank_difference: 1, log2fc: 2.36, adj_pvalue: 1.8e-6, priority_score: 75.3, priority_level: 'MEDIUM', has_grn: true, regulation: 'upregulated', top_cell_type: 'immune cells' },
        { symbol: 'HNF1A-AS1', expression_rank: 5, priority_rank: 6, ranking_category: 'TOP_EXPRESSION', rank_difference: -1, log2fc: 1.94, adj_pvalue: 4.2e-5, priority_score: 71.8, priority_level: 'MEDIUM', has_grn: true, regulation: 'upregulated', top_cell_type: 'ductal cells' },
        { symbol: 'SAMMSON', expression_rank: 12, priority_rank: 7, ranking_category: 'TOP_NETWORK', rank_difference: 5, log2fc: 2.56, adj_pvalue: 9.1e-7, priority_score: 68.5, priority_level: 'MEDIUM', has_grn: true, regulation: 'upregulated', top_cell_type: 'melanocytes' },
        { symbol: 'MIAT', expression_rank: 7, priority_rank: 8, ranking_category: 'DIVERGENT', rank_difference: -1, log2fc: 1.74, adj_pvalue: 8.3e-5, priority_score: 65.2, priority_level: 'MEDIUM', has_grn: true, regulation: 'upregulated', top_cell_type: 'stromal cells' },
        { symbol: 'LINC01480', expression_rank: 8, priority_rank: 9, ranking_category: 'TOP_EXPRESSION', rank_difference: -1, log2fc: 2.44, adj_pvalue: 2.5e-6, priority_score: 62.0, priority_level: 'MEDIUM', has_grn: true, regulation: 'upregulated', top_cell_type: 'fibroblasts' },
        { symbol: 'NEAT1', expression_rank: 15, priority_rank: 10, ranking_category: 'TOP_NETWORK', rank_difference: 5, log2fc: 1.89, adj_pvalue: 6.7e-5, priority_score: 58.9, priority_level: 'MEDIUM', has_grn: true, regulation: 'upregulated', top_cell_type: 'macrophages' },
      ],
      statistics: {
        n_total: 47,
        n_consensus: 8,
        n_top_expression: 14,
        n_top_network: 15,
        n_divergent: 10,
        n_disease_cells: 4521,
        n_normal_cells: 6832,
        response_time_ms: 1850,
      },
      cell_types: ['mast cells', 'epithelial cells', 'luminal cells', 'basal cells', 'immune cells', 'ductal cells', 'stromal cells', 'fibroblasts', 'macrophages'],
    },
  },

  // ── Step 3: Knockdown Simulation ──
  {
    label: 'Knockdown Simulation',
    userQuery: 'Simulate knockdown of LINC01871 in breast cancer in mast cells',
    assistantText:
      'Here are the results of the **in silico knockdown** of LINC01871 in breast cancer mast cells.\n\nThe simulation predicts a **rescue score of 72.5%** (HIGH effectiveness), meaning silencing LINC01871 would reverse approximately 72.5% of the disease-associated dysregulation in downstream genes. Key rescued genes include TP53, ESR1, and BRCA1, with enrichment in estrogen signaling and JAK-STAT pathways.',
    visualization: {
      type: 'knockdown',
      metadata: { lncrna: 'LINC01871', disease: 'breast cancer' },
      rescue_score: {
        score: 72.5,
        effectiveness: 'HIGH',
        pathways: [
          { name: 'Estrogen signaling pathway', pathway: 'hsa04915', term: 'Estrogen signaling pathway', impact_score: 15.3, rescue_percentage: 18.2, database: 'KEGG', adj_p_value: 0.0012, p_value: 0.00045 },
          { name: 'JAK-STAT signaling pathway', pathway: 'hsa04630', term: 'JAK-STAT signaling pathway', impact_score: 12.1, rescue_percentage: 14.5, database: 'KEGG', adj_p_value: 0.0089, p_value: 0.0034 },
          { name: 'PI3K-Akt signaling pathway', pathway: 'hsa04151', term: 'PI3K-Akt signaling pathway', impact_score: 10.8, rescue_percentage: 12.3, database: 'KEGG', adj_p_value: 0.015, p_value: 0.0067 },
          { name: 'p53 signaling pathway', pathway: 'hsa04115', term: 'p53 signaling pathway', impact_score: 9.4, rescue_percentage: 10.1, database: 'KEGG', adj_p_value: 0.023, p_value: 0.011 },
        ],
      },
      rescued_genes: [
        { gene: 'TP53', improvement: 87.3, log2fc_magnitude: 3.12, original_dysregulation: 3.12, direction: 'down' },
        { gene: 'ESR1', improvement: 76.5, log2fc_magnitude: 2.84, original_dysregulation: 2.84, direction: 'up' },
        { gene: 'BRCA1', improvement: 65.4, log2fc_magnitude: 2.15, original_dysregulation: 2.15, direction: 'down' },
        { gene: 'CCND1', improvement: 58.2, log2fc_magnitude: 1.93, original_dysregulation: 1.93, direction: 'up' },
        { gene: 'MYC', improvement: 52.1, log2fc_magnitude: 1.78, original_dysregulation: 1.78, direction: 'up' },
      ],
      overall_rescue_distance: 68.5,
      n_dysregulated: 1247,
      n_rescued: 856,
      affected_genes: [
        { gene: 'TP53', mean_change: 0.34, expression_change: 0.34, direction: 'down' },
        { gene: 'ESR1', mean_change: -0.28, expression_change: -0.28, direction: 'up' },
        { gene: 'BRCA1', mean_change: 0.52, expression_change: 0.52, direction: 'down' },
        { gene: 'CCND1', mean_change: -0.21, expression_change: -0.21, direction: 'up' },
        { gene: 'MYC', mean_change: -0.18, expression_change: -0.18, direction: 'up' },
        { gene: 'CDKN1A', mean_change: 0.45, expression_change: 0.45, direction: 'down' },
        { gene: 'BCL2', mean_change: -0.31, expression_change: -0.31, direction: 'up' },
        { gene: 'VEGFA', mean_change: -0.15, expression_change: -0.15, direction: 'up' },
      ],
      rescue_distance_chart: { baseline_score: 2.34, knockdown_score: 0.742 },
    },
  },

  // ── Step 4: Network Exploration ──
  {
    label: 'Network Exploration',
    userQuery: 'Show me the gene regulatory network for NEAT1 in lung cancer in macrophages',
    assistantText:
      'Here is the **gene regulatory network** for NEAT1 in lung cancer macrophages.\n\nThe disease-specific GRN contains **18 nodes** and **24 edges**, with NEAT1 acting as a central hub connected to key immune regulators. Notable connections include STAT3, NF-κB pathway genes, and the FDA drug target TNF. The network shows significant disease-specific rewiring compared to normal tissue, with an enrichment score of **0.847**.',
    visualization: {
      type: 'network',
      metadata: { lncrna: 'NEAT1', disease: 'lung cancer' },
      disease_grn: {
        nodes: [
          { id: 'NEAT1', label: 'NEAT1', type: 'lncrna' },
          { id: 'STAT3', label: 'STAT3', type: 'protein_coding' },
          { id: 'TNF', label: 'TNF', type: 'fda_drug_target' },
          { id: 'IL6', label: 'IL6', type: 'fda_drug_target' },
          { id: 'NFKB1', label: 'NFKB1', type: 'protein_coding' },
          { id: 'HIF1A', label: 'HIF1A', type: 'protein_coding' },
          { id: 'VEGFA', label: 'VEGFA', type: 'fda_drug_target' },
          { id: 'MMP9', label: 'MMP9', type: 'protein_coding' },
          { id: 'CXCL8', label: 'CXCL8', type: 'protein_coding' },
          { id: 'CCL2', label: 'CCL2', type: 'protein_coding' },
          { id: 'TP53', label: 'TP53', type: 'protein_coding' },
          { id: 'JAK2', label: 'JAK2', type: 'fda_drug_target' },
          { id: 'EGFR', label: 'EGFR', type: 'fda_drug_target' },
          { id: 'MYC', label: 'MYC', type: 'protein_coding' },
          { id: 'BCL2', label: 'BCL2', type: 'fda_drug_target' },
          { id: 'TGFB1', label: 'TGFB1', type: 'protein_coding' },
          { id: 'CDH1', label: 'CDH1', type: 'protein_coding' },
          { id: 'SPP1', label: 'SPP1', type: 'protein_coding' },
        ],
        edges: [
          { source: 'NEAT1', target: 'STAT3', weight: 0.91 },
          { source: 'NEAT1', target: 'TNF', weight: 0.87 },
          { source: 'NEAT1', target: 'NFKB1', weight: 0.84 },
          { source: 'NEAT1', target: 'IL6', weight: 0.82 },
          { source: 'NEAT1', target: 'HIF1A', weight: 0.79 },
          { source: 'NEAT1', target: 'TP53', weight: 0.76 },
          { source: 'NEAT1', target: 'EGFR', weight: 0.73 },
          { source: 'STAT3', target: 'JAK2', weight: 0.88 },
          { source: 'STAT3', target: 'MYC', weight: 0.75 },
          { source: 'STAT3', target: 'BCL2', weight: 0.71 },
          { source: 'TNF', target: 'NFKB1', weight: 0.85 },
          { source: 'TNF', target: 'CXCL8', weight: 0.78 },
          { source: 'TNF', target: 'CCL2', weight: 0.74 },
          { source: 'NFKB1', target: 'IL6', weight: 0.80 },
          { source: 'NFKB1', target: 'MMP9', weight: 0.72 },
          { source: 'HIF1A', target: 'VEGFA', weight: 0.86 },
          { source: 'HIF1A', target: 'MMP9', weight: 0.68 },
          { source: 'IL6', target: 'JAK2', weight: 0.81 },
          { source: 'EGFR', target: 'MYC', weight: 0.70 },
          { source: 'TP53', target: 'BCL2', weight: 0.67 },
          { source: 'TP53', target: 'CDH1', weight: 0.64 },
          { source: 'TGFB1', target: 'SPP1', weight: 0.69 },
          { source: 'TGFB1', target: 'MMP9', weight: 0.66 },
          { source: 'VEGFA', target: 'SPP1', weight: 0.62 },
        ],
        n_nodes: 18,
        n_edges: 24,
      },
      normal_grn: {
        nodes: [
          { id: 'NEAT1', label: 'NEAT1', type: 'lncrna' },
          { id: 'STAT3', label: 'STAT3', type: 'protein_coding' },
          { id: 'TNF', label: 'TNF', type: 'fda_drug_target' },
          { id: 'NFKB1', label: 'NFKB1', type: 'protein_coding' },
          { id: 'TP53', label: 'TP53', type: 'protein_coding' },
          { id: 'BCL2', label: 'BCL2', type: 'fda_drug_target' },
        ],
        edges: [
          { source: 'NEAT1', target: 'STAT3', weight: 0.42 },
          { source: 'NEAT1', target: 'TP53', weight: 0.38 },
          { source: 'STAT3', target: 'BCL2', weight: 0.35 },
          { source: 'TNF', target: 'NFKB1', weight: 0.51 },
        ],
        n_nodes: 6,
        n_edges: 4,
      },
      statistics: {
        total_nodes: 18,
        total_edges: 24,
        lncrna_count: 1,
        avg_degree: 2.67,
        hub_genes: ['STAT3', 'TNF', 'NFKB1', 'HIF1A', 'TP53'],
        fda_targets: 6,
        enrichment_score: 0.847,
        metrics: {
          delta_edges: 20,
          delta_protein_coding: 12,
          specificity_ratio: 3.0,
        },
      },
    },
  },
];
